import React from 'react';
import cn from 'classnames';
import { Pill } from '@els/els-react--pill';
import { Flyout } from '@els/els-react--flyout';
import noop from 'lodash/noop';

import RsTableToolbar from '@Pages/RetroSynthesis/components/RsTableToolbar';
import { ButtonWithIcon } from '@els/els-react--button';
import { RouteType } from '@Pages/RetroSynthesis/constants';
import PropTypes from 'prop-types';
import { ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from '../constants';
import ZoomButtons from './ZoomButtons';

import './ProjectRouteTreeViewTopPanel.scss';

const routeTypeClassName = (routeType) => {
  switch (routeType) {
    case RouteType.PUBLISHED:
      return 'project-route-tree-view-top-panel__label__published';
    case RouteType.PREDICTED:
      return 'project-route-tree-view-top-panel__label__predicted';
    case RouteType.CUSTOMIZED:
      return 'project-route-tree-view-top-panel__label__customized';
    case RouteType.COMBINED:
      return 'project-route-tree-view-top-panel__label__combined';
    default:
      return null;
  }
};

const routeTypeLabel = (routeType, routeId) => {
  switch (routeType) {
    case RouteType.PUBLISHED:
      return `Published route #${routeId}`;
    case RouteType.PREDICTED:
      return `Predicted route #${routeId}`;
    case RouteType.CUSTOMIZED:
      return `Customized route #${routeId}`;
    case RouteType.COMBINED:
      return (
        <>
          <Flyout
            flyout={
              <div className="rs-tooltip-big__content">
                <h4 className="u-els-margin-bottom">Combined routes</h4>
                <p className="u-els-margin-bottom-1o2">
                  Combines published and predicted routes, adding literature steps for non-commercial intermediates.
                </p>
                <ul className="c-els-list">
                  <li className="c-els-list__item">Published routes are selected based on the lowest-cost starting materials</li>
                  <li className="c-els-list__item">If multiple options have the same cost, the shortest route is chosen.</li>
                  <li className="c-els-list__item">
                    Includes up to five literature steps, integrated with your initial starting-material settings (10-day delivery or whole
                    library)
                  </li>
                </ul>
              </div>
            }
            shouldCloseOnESC
            placement="bottom-end"
            pointerType="primary"
            showCloseButton
            inline
            width="400px">
            <ButtonWithIcon
              className="u-els-margin-right-1o4 e2e-info-tooltip-combined-route"
              sprite="InformationOutlineCircle"
              iconOnly
              type="link"
              linkIconSize="xs"
              linkOptions={['navigation']}
              onClick={noop}>
              Combined routes info tooltip
            </ButtonWithIcon>
          </Flyout>
          <span>Combined route #{routeId}</span>
        </>
      );
    default:
      return null;
  }
};

function ProjectRouteTreeViewTopPanel({
  treeViewRef,
  zoom,
  zoomOnChange,
  rotateOnClick = noop,
  fitOnClick = noop,
  copyOnClick = noop,
  routeId,
  routeType,
}) {
  const hoverTreeNodesClass = 'project-route-tree--hover-tree';
  const addHoverClass = () => {
    treeViewRef?.current?.classList?.add(hoverTreeNodesClass);
  };
  const removeHoverClass = () => {
    treeViewRef?.current?.classList?.remove(hoverTreeNodesClass);
  };

  return (
    <RsTableToolbar className="rs-table-toolbar--transparent project-route-tree-view-top-panel">
      <div className="rs-table-toolbar__left-controls project-route-tree-view-top-panel__controls">
        <ButtonWithIcon
          layout="vertical"
          sprite="Rotate"
          type="borderless"
          size="small"
          className="e2e-rs-toolbar-button-rotate"
          onClick={rotateOnClick}>
          Rotate
        </ButtonWithIcon>
        <ButtonWithIcon
          layout="vertical"
          sprite="ScaleUp"
          type="borderless"
          size="small"
          className="e2e-rs-toolbar-button-fit-view"
          onClick={fitOnClick}>
          Fit view
        </ButtonWithIcon>
        <ButtonWithIcon
          layout="vertical"
          sprite="DuplicateDocument"
          type="borderless"
          size="small"
          className="e2e-rs-toolbar-button-copy-route"
          onClick={copyOnClick}
          onMouseEnter={addHoverClass}
          onMouseLeave={removeHoverClass}
          onFocus={addHoverClass}
          onBlur={removeHoverClass}>
          Copy route
        </ButtonWithIcon>
        <ZoomButtons
          className="project-route-tree-view-top-panel__slider"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step={ZOOM_STEP}
          value={zoom}
          onChanged={zoomOnChange}
        />
      </div>
      <div className="rs-table-toolbar__center-controls" />
      {routeTypeLabel(routeType, routeId) && (
        <Pill
          className={cn('rs-table-toolbar__right-controls project-route-tree-view-top-panel__label', routeTypeClassName(routeType))}
          textColor="text">
          <span className="c-els-position-relative">{routeTypeLabel(routeType, routeId)}</span>
        </Pill>
      )}
    </RsTableToolbar>
  );
}

ProjectRouteTreeViewTopPanel.propTypes = {
  treeViewRef: PropTypes.object,
  zoom: PropTypes.number,
  zoomOnChange: PropTypes.func,
  rotateOnClick: PropTypes.func,
  fitOnClick: PropTypes.func,
  copyOnClick: PropTypes.func,
  routeId: PropTypes.string,
  routeType: PropTypes.string,
};

export default ProjectRouteTreeViewTopPanel;
